import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import PDFDocument, { PDFConfig } from '../base/PDFDocument';
import PDFPatientInfo from '../base/PDFPatientInfo';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles } from '../styles/typography';
import { createTwoColumnLayout } from '../styles/layout';
import { PDFSurveyRecordRenderer } from '../utils/contentRenderer';
import { formatDateForPDF, formatPatientName } from '../utils/dataFormatters';
import { createT } from '../utils/simpleTranslate';
import { PDFFieldRenderer } from '../utils/PDFFieldRenderer';

// Helper function for researcher names
const formatResearcherName = (researcher: IResearcher): string => {
  return `${researcher.name} ${researcher.surnames}`.trim();
};

/**
 * Submission PDF Document Component
 * 
 * Handles patient survey submissions and form responses.
 * Displays survey sections with their corresponding field responses.
 */

interface IPatient {
  personalData: {
    name?: string;
    surnames?: string;
    birthdate?: string | Date;
    sex?: string;
    phone?: string;
    healthId?: string;
    nationalId?: string;
    passportNumber?: string;
    insurance?: string | number;
  };
}

interface IResearcher {
  uuid: string;
  name: string;
  surnames: string;
}

interface ISurveyField {
  id: string | number;
  uuid?: string;
  name: string;
  label: string;
  type: string;
  order: number;
  options?: string[] | any[];
}

interface ISurveySection {
  id?: number;
  uuid: string;
  name: string;
  order: number;
  fields: ISurveyField[];
}

interface ISurvey {
  uuid: string;
  name: string;
  sections: { [key: string]: ISurveySection } | ISurveySection[];
}

interface ISurveyRecord {
  id: string | number;
  value: any;
  surveyField: ISurveyField;
  surveySection?: any;
}

interface ISubmission {
  id: string | number;
  createdAt: string | Date;
  updatedAt: string | Date;
  uuidResearcher?: string;
  researcher?: IResearcher | string;
  records?: ISurveyRecord[];
  surveyRecords?: ISurveyRecord[];
}

interface SubmissionPDFDocumentProps {
  submission: ISubmission;
  currentSurvey: ISurvey;
  patient: IPatient;
  prescribingDoctor?: IResearcher;
  config: PDFConfig;
  nameSurvey: string;
  locale?: string;
  uuidInvestigation: string; // Investigation UUID (for reference/tracking)
  signatureBase64?: string | null; // Base64-encoded signature image from parent component (if available)
}

export const SubmissionPDFDocument: React.FC<SubmissionPDFDocumentProps> = ({
  submission,
  currentSurvey,
  patient,
  prescribingDoctor,
  config,
  nameSurvey,
  locale = 'fr',
  uuidInvestigation,
  signatureBase64,
}) => {
  const t = createT(locale);

  // Group records by section
  const recordsBySection = React.useMemo(() => {
    const records = submission.records || submission.surveyRecords || [];
    if (!records.length) return {};
    
    const grouped: { [sectionUuid: string]: ISurveyRecord[] } = {};
    const sections = Array.isArray(currentSurvey.sections) 
      ? currentSurvey.sections 
      : Object.values(currentSurvey.sections);
    
    records.forEach(record => {
      // Find which section this field belongs to
      const section = sections.find(s => 
        s.fields.some(f => f.id === record.surveyField.id || f.id === (record.surveyField as any).uuid)
      );
      
      if (section) {
        if (!grouped[section.uuid]) {
          grouped[section.uuid] = [];
        }
        grouped[section.uuid].push(record);
      }
    });
    
    return grouped;
  }, [submission, currentSurvey.sections]);

  const renderSurveySection = (section: ISurveySection) => {
    const sectionRecords = recordsBySection[section.uuid] || [];
    
    return (
      <View key={section.uuid} style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {section.name}
        </Text>
        <View style={commonPDFStyles.sectionContent}>
          {section.fields
            .sort((a, b) => a.order - b.order)
            .map(field => {
              const record = sectionRecords.find(r => r.surveyField.id === field.id);
              return renderSurveyField(field, record);
            })}
        </View>
      </View>
    );
  };

  const renderSurveyField = (field: ISurveyField, record?: ISurveyRecord) => {
    const sectionCount = Array.isArray(currentSurvey.sections) 
      ? currentSurvey.sections.length 
      : Object.keys(currentSurvey.sections).length;
    const showFieldName = currentSurvey.sections && sectionCount > 1;
    
    return (
      <View key={field.id} style={[commonPDFStyles.marginBottom]}>
        {showFieldName && (
          <Text style={typographyStyles.subtitle2}>
            {field.name}:
          </Text>
        )}
        
        <View style={{ paddingLeft: 8 }}>
          {renderFieldValue(field, record)}
        </View>
      </View>
    );
  };

  const renderFieldValue = (field: ISurveyField, record?: ISurveyRecord) => {
    // Use unified field renderer
    return (
      <PDFFieldRenderer
        field={field}
        value={record?.value}
        locale={locale}
      />
    );
  };

  const renderSubmissionInfo = () => {
    return (
      <View style={[commonPDFStyles.section]}>
        <Text style={typographyStyles.h4}>
          {t('hospital.submission.info')}
        </Text>
        
        <View style={commonPDFStyles.sectionContent}>
          <Text style={typographyStyles.body2}>
            <Text style={typographyStyles.button}>{t('pages.request.pdf.created_at')}</Text>: {formatDateForPDF(submission.createdAt, locale)}
          </Text>
          
          {submission.updatedAt !== submission.createdAt && (
            <Text style={typographyStyles.body2}>
              <Text style={typographyStyles.button}>{t('pages.request.pdf.updated_at')}</Text>: {formatDateForPDF(submission.updatedAt, locale)}
            </Text>
          )}
        </View>
      </View>
    );
  };

  const renderFooter = () => {
    if (!prescribingDoctor) return null;

    return (
      <View style={[commonPDFStyles.signature, commonPDFStyles.pageBreakAvoid]}>
        {/* Render signature image if available (must be base64-encoded) */}
        {signatureBase64 && (
          <View style={{ marginBottom: 10, alignItems: 'center' }}>
            <Image 
              src={signatureBase64} 
              style={{ 
                width: 150, 
                height: 75, 
                objectFit: 'contain',
              }} 
            />
          </View>
        )}
        
        {/* Signature text (always shown) */}
        <Text style={commonPDFStyles.signatureName}>
          {t('general.signature')}
        </Text>
        <Text style={commonPDFStyles.signatureName}>
          {formatResearcherName(prescribingDoctor)}
        </Text>
        <Text style={commonPDFStyles.signatureTitle}>
          {t('hospital.doctor.prescribing_doctor')}
        </Text>
      </View>
    );
  };

  // Sort sections by order - handle both array and object structures
  const sortedSections = (Array.isArray(currentSurvey.sections) 
    ? currentSurvey.sections 
    : Object.values(currentSurvey.sections)
  ).sort((a, b) => a.order - b.order);

  return (
    <PDFDocument
      title={nameSurvey}
      patientInfo={<PDFPatientInfo patient={patient} locale={locale} />}
      config={config}
      footer={renderFooter()}
    >
      {renderSubmissionInfo()}
      
      {sortedSections.map(section => renderSurveySection(section))}
    </PDFDocument>
  );
};

export default SubmissionPDFDocument;
